// pages/admin/AdminPanel.jsx
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Home, LogOut, Loader } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabaseClient'
import { useAuth } from '../../contexts/AuthContext'
import ActualiteEditor from '../../components/Admin/ActualiteEditor'
import RolesManager from '../../components/Admin/RolesManager'
import CategoriesManager from '../../components/Admin/CategoriesManager'
import ActuCfaEditor from '../../components/Admin/ActuCfaEditor'


const AdminPanel = () => {
  const navigate = useNavigate()
  const { userRole, loading, user } = useAuth()
  const [activeTab, setActiveTab] = useState('actualites')
  const [selectedType, setSelectedType] = useState('Vente')
  const [editingId, setEditingId] = useState(null)
  const [showEditor, setShowEditor] = useState(false)
  const [actualites, setActualites] = useState([])
  const [loadingActualites, setLoadingActualites] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  // États pour Actualités CFA
  const [actusCfa, setActusCfa] = useState([])
  const [loadingActusCfa, setLoadingActusCfa] = useState(false)
  const [showEditorCfa, setShowEditorCfa] = useState(false)
  const [editingIdCfa, setEditingIdCfa] = useState(null)

  // Vérification des permissions
  useEffect(() => {
    if (!loading && (!user || !['super_admin', 'admin', 'formateur'].includes(userRole))) {
      navigate('/')
    }
  }, [loading, userRole, user, navigate])

  // Charger les actualités
useEffect(() => {
  if (activeTab === 'actualites') {
    fetchActualites()
  } else if (activeTab === 'actualites-cfa') {
    fetchActusCfa()
  }
}, [activeTab, selectedType])

  const fetchActualites = async () => {
    setLoadingActualites(true)
    try {
      let query = supabase
        .from('actualites')
        .select('*')
        .eq('type', selectedType)

      // Les formateurs voient seulement leurs actualités
      if (userRole === 'formateur') {
        query = query.eq('created_by', user.id)
      }

      const { data, error } = await query.order('created_at', { ascending: false })

      if (error) throw error
      setActualites(data || [])
    } catch (error) {
      console.error('❌ Erreur fetchActualites:', error)
    } finally {
      setLoadingActualites(false)
    }
  }

  const fetchActusCfa = async () => {
  setLoadingActusCfa(true)
  try {
    let query = supabase
      .from('actu_cfa')
      .select(`
        *,
        profiles:auteur_id (
          first_name,
          last_name,
          email
        )
      `)

    // Les formateurs voient seulement leurs actualités
    if (userRole === 'formateur') {
      query = query.eq('auteur_id', user.id)
    }

    const { data, error } = await query.order('date_publication', { ascending: false })

    if (error) throw error
    setActusCfa(data || [])
  } catch (error) {
    console.error('❌ Erreur fetchActusCfa:', error)
  } finally {
    setLoadingActusCfa(false)
  }
}

  const handleLogout = async () => {
    console.log('🔴 handleLogout appelé')
    console.log('👤 User actuel:', user)
    console.log('📧 Email:', user?.email)
    
    setLoggingOut(true)
    
    try {
      console.log('🔄 Tentative de déconnexion Supabase...')
      
      const { error } = await supabase.auth.signOut()
      
      if (error) {
        console.error('❌ Erreur signOut:', error)
        console.error('❌ Erreur message:', error.message)
        console.error('❌ Erreur status:', error.status)
        alert(`Erreur déconnexion: ${error.message}`)
        setLoggingOut(false)
        return
      }
      
      console.log('✅ Déconnexion Supabase réussie')
      console.log('🔄 Navigation vers /login')
      
      setLoggingOut(false)
      navigate('/login')
    } catch (error) {
      console.error('❌ Exception logout:', error)
      console.error('❌ Exception message:', error.message)
      alert(`Exception: ${error.message}`)
      setLoggingOut(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-green-500" />
      </div>
    )
  }

  // Affichage de l'éditeur
  if (showEditor) {
    return (
      <div className="min-h-screen bg-slate-900 p-6">
        <ActualiteEditor
          actualiteId={editingId}
          type={selectedType}
          onBack={() => {
            setShowEditor(false)
            setEditingId(null)
            fetchActualites()
          }}
        />
      </div>
    )
  }

  // Affichage de l'éditeur CFA
if (showEditorCfa) {
  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <ActuCfaEditor
        actualiteId={editingIdCfa}
        onBack={() => {
          setShowEditorCfa(false)
          setEditingIdCfa(null)
          fetchActusCfa()
        }}
      />
    </div>
  )
}

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
    >
      {/* Header */}
      <div className="sticky top-0 z-50 bg-slate-800/90 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <Home className="w-5 h-5 text-gray-400" />
            </motion.button>
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-gray-400">Connecté en tant que</p>
              <p className="text-white font-semibold">{user?.email}</p>
              <p className="text-xs text-green-400 uppercase">{userRole}</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleLogout}
              disabled={loggingOut}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                loggingOut
                  ? 'bg-red-500 text-white cursor-not-allowed opacity-50'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              {loggingOut ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Déconnexion...
                </>
              ) : (
                <>
                  <LogOut className="w-4 h-4" />
                  Déconnexion
                </>
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 bg-slate-800/60 p-3 rounded-lg backdrop-blur-sm">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setActiveTab('actualites')
              setShowEditor(false)
            }}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'actualites'
                ? 'bg-green-600 text-white'
                : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
            }`}
          >
            Actualités
          </motion.button>

          {/* Onglet Actualités CFA */}
<motion.button
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.95 }}
  onClick={() => {
    setActiveTab('actualites-cfa')
    setShowEditor(false)
    setShowEditorCfa(false)
  }}
  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
    activeTab === 'actualites-cfa'
      ? 'bg-orange-600 text-white'
      : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
  }`}
>
  Actualités CFA
</motion.button>

          {['super_admin', 'admin'].includes(userRole) && (
            <>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setActiveTab('categories')
                  setShowEditor(false)
                }}
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'categories'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                }`}
              >
                Catégories
              </motion.button>

              {userRole === 'super_admin' && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setActiveTab('roles')
                    setShowEditor(false)
                  }}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    activeTab === 'roles'
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                  }`}
                >
                  Rôles
                </motion.button>
              )}
            </>
          )}
        </div>

        {/* Contenu des tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          key={activeTab}
        >
          {/* Tab Actualités */}
          {activeTab === 'actualites' && (
            <div className="space-y-6">
              {/* Filtres */}
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-slate-800/60 p-4 rounded-lg backdrop-blur-sm">
                <div className="flex gap-2">
                  {['Vente', 'Auto'].map(type => (
                    <motion.button
                      key={type}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setSelectedType(type)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedType === type
                          ? 'bg-green-600 text-white'
                          : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                      }`}
                    >
                      {type}
                    </motion.button>
                  ))}
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setEditingId(null)
                    setShowEditor(true)
                  }}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  + Nouvelle actualité
                </motion.button>
              </div>

              {/* Liste actualités */}
              {loadingActualites ? (
                <div className="flex justify-center">
                  <Loader className="w-8 h-8 animate-spin text-green-500" />
                </div>
              ) : actualites.length === 0 ? (
                <div className="text-center text-gray-400 py-12 bg-slate-800/60 rounded-lg">
                  Aucune actualité pour cette catégorie
                </div>
              ) : (
                <div className="grid gap-4">
                  {actualites.map(actualite => (
                    <motion.div
                      key={actualite.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-slate-800/60 rounded-lg p-4 flex items-center justify-between hover:bg-slate-700/60 transition-colors"
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-white mb-1">{actualite.titre}</h4>
                        <p className="text-sm text-gray-400 line-clamp-1">{actualite.description}</p>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setEditingId(actualite.id)
                          setShowEditor(true)
                        }}
                        className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                      >
                        Modifier
                      </motion.button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab Catégories */}
          {activeTab === 'categories' && (
            <div className="space-y-6">
              <CategoriesManager type={selectedType} />
            </div>
          )}

          {/* Tab Rôles */}
          {activeTab === 'roles' && (
            <RolesManager />
          )}

          {/* Tab Actualités CFA */}
          {activeTab === 'actualites-cfa' && (
            <div className="space-y-6">
              {/* Bouton Nouvelle Actualité */}
              <div className="flex justify-end bg-slate-800/60 p-4 rounded-lg backdrop-blur-sm">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setEditingIdCfa(null)
                    setShowEditorCfa(true)
                  }}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  + Nouvelle actualité CFA
                </motion.button>
              </div>

              {/* Liste actualités CFA */}
              {loadingActusCfa ? (
                <div className="flex justify-center">
                  <Loader className="w-8 h-8 animate-spin text-green-500" />
                </div>
              ) : actusCfa.length === 0 ? (
                <div className="text-center text-gray-400 py-12 bg-slate-800/60 rounded-lg">
                  Aucune actualité CFA
                </div>
              ) : (
                <div className="grid gap-4">
                  {actusCfa.map(actualite => (
                    <motion.div
                      key={actualite.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-slate-800/60 rounded-lg p-4 flex flex-col md:flex-row items-start md:items-center gap-4 hover:bg-slate-700/60 transition-colors"
                    >
                      {/* Image */}
                      {actualite.image_url && (
                        <img
                          src={actualite.image_url}
                          alt={actualite.titre}
                          className="w-full md:w-32 h-20 object-cover rounded-lg flex-shrink-0"
                        />
                      )}

                      {/* Contenu */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-green-600/20 text-green-400 text-xs rounded">
                            {actualite.categorie}
                          </span>
                          {actualite.is_featured && (
                            <span className="px-2 py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded flex items-center gap-1">
                              ⭐ À la une
                            </span>
                          )}
                          <span className="text-xs text-gray-400">
                            {new Date(actualite.date_publication).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <h4 className="font-semibold text-white mb-1 line-clamp-1">{actualite.titre}</h4>
                        <p className="text-sm text-gray-400 line-clamp-2">{actualite.contenu?.substring(0, 150)}...</p>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 flex-shrink-0">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => {
                            setEditingIdCfa(actualite.id)
                            setShowEditorCfa(true)
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                        >
                          Modifier
                        </motion.button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  )
}

export default AdminPanel